/*
scrollWrapper.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import {
  useRef,
  useEffect,
  PropsWithChildren,
  useImperativeHandle,
  forwardRef,
  memo,
  useCallback,
} from 'react'
import type { CSSProperties } from 'react'
import iScroll, { Options as IScrollOptions } from 'iscroll'
import './scrollWrapper.scss'
import { getBrowserType } from '../modules/deviceModel';
import { deduplicateEvent } from '../modules/deduplicateEvent';
import { BrowserTypeEnum } from '../types/browserTypeEnum';

export const scrollerClickEvent = 'ScrollerClick';
export interface IScrollWrapperProps {
  options?: IScrollOptions
  className?: string,
  scrollId?: string,
  horizontal?: boolean,
  style?: CSSProperties,
  contentClassName?: string,
  contentStyle?: CSSProperties,
  onScrollStart?: (details: { x: number; y: number; scrollId?: string }) => void
  onScroll?: (details: { x: number; y: number; scrollId?: string }) => void
  autoRefreshOnChildrenChange?: boolean
}
export interface ScrollWrapperRef {
  scrollTo: (position: number) => void // Exposed function to scroll to a position
  refresh: () => void
}
export type ScrollWrapperProps = PropsWithChildren<IScrollWrapperProps>

const ScrollWrapper = forwardRef<
  ScrollWrapperRef,
  ScrollWrapperProps
>(({ children, options, scrollId, horizontal = false, className, style, contentClassName, contentStyle, onScrollStart, onScroll, autoRefreshOnChildrenChange = true }, ref) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null) // To track content size
  const iScrollInstance = useRef<iScroll | null>(null)
  const scrollStartCallbackRef = useRef<IScrollWrapperProps['onScrollStart']>(onScrollStart)
  const scrollCallbackRef = useRef<IScrollWrapperProps['onScroll']>(onScroll)
  const preventMousedown = useCallback((event: MouseEvent): void => {
    if (event.cancelable) {
      event.preventDefault()
    }
  }, [])
  useEffect(() => {
    scrollStartCallbackRef.current = onScrollStart
  }, [onScrollStart])
  useEffect(() => {
    scrollCallbackRef.current = onScroll
  }, [onScroll])

  const isTouchDevice = window.matchMedia('(pointer: coarse)').matches

  const browserType = getBrowserType()

  useImperativeHandle(ref, () => ({
    scrollTo: (position: number) => {
      if (iScrollInstance.current) {
        const x = horizontal ? position : 0
        const y = horizontal ? 0 : position
        iScrollInstance.current.scrollTo(x, y)
        iScrollInstance.current.refresh()
      }
    },
    refresh: () => {
      iScrollInstance.current?.refresh()
    }
  }), [horizontal])
  const handleResize = useCallback(() => {
    if (iScrollInstance.current) {
      iScrollInstance.current.refresh()
    }
  }, [])
  const handleScroll = useCallback((event: Event) => {
    event.stopPropagation()
  }, [])
  const handleModalClose = useCallback(() => {
    setTimeout(() => iScrollInstance.current?.refresh(), 1500)
  }, [])
  const mergedOptions: IScrollOptions = {
    ...options,
      disableTouch: !isTouchDevice,
      disableMouse: isTouchDevice,
      disablePointer: browserType == BrowserTypeEnum.ThirdGen ? true : isTouchDevice,
    // useTransition: false,
    // bounce: false,
    // momentum: false,
    scrollbars: true,
    interactiveScrollbars: true,
    fadeScrollbars: false,
    mouseWheel: options?.mouseWheel ?? true,
    bindToWrapper: true,
    tap: true,
    click: true // for iScroll to allow native click events
    //preventDefault: false
  }
  if (horizontal) {
    mergedOptions.scrollX = true
    mergedOptions.scrollY = false
    mergedOptions.eventPassthrough = options?.eventPassthrough ?? 'vertical'
    mergedOptions.wheelAction = options?.wheelAction ?? 'scrollX'
  } else {
    mergedOptions.scrollX = options?.scrollX ?? false
    mergedOptions.scrollY = options?.scrollY ?? true
  }

  useEffect(() => {
    const currentScrollContainer = scrollContainerRef.current
    if (currentScrollContainer) {
      iScrollInstance.current = new iScroll(
        currentScrollContainer,
        mergedOptions
      )
      const notifyScrollStart = () => {
        if (!iScrollInstance.current) {
          return
        }
        const handler = scrollStartCallbackRef.current
        if (!handler) {
          return
        }
        handler({
          x: iScrollInstance.current.x,
          y: iScrollInstance.current.y,
          scrollId
        })
      }
      iScrollInstance.current.on('scrollStart', notifyScrollStart)
      let rafId: number | null = null
      const schedule =
        window.requestAnimationFrame?.bind(window) ??
        ((cb: FrameRequestCallback) =>
          window.setTimeout(() => cb(Date.now()), 16))
      const cancelSchedule =
        window.cancelAnimationFrame?.bind(window) ??
        ((id: number) => window.clearTimeout(id))
      const notifyScroll = () => {
        if (!iScrollInstance.current) {
          return
        }
        const handler = scrollCallbackRef.current
        if (!handler) {
          return
        }
        handler({
          x: iScrollInstance.current.x,
          y: iScrollInstance.current.y,
          scrollId
        })
      }
      let pollId: number | null = null
      const startPolling = () => {
        if (pollId !== null || !scrollCallbackRef.current) {
          return
        }
        pollId = window.setInterval(() => {
          notifyScroll()
        }, 50)
      }
      const stopPolling = () => {
        if (pollId === null) {
          return
        }
        window.clearInterval(pollId)
        pollId = null
      }
      const scheduleScroll = () => {
        if (rafId !== null) {
          return
        }
        rafId = schedule(() => {
          rafId = null
          notifyScroll()
        })
      }
      iScrollInstance.current.on('scroll', scheduleScroll)
      const handleScrollEnd = () => {
        stopPolling()
        scheduleScroll()
      }
      iScrollInstance.current.on('scrollStart', startPolling)
      iScrollInstance.current.on('scrollEnd', handleScrollEnd)

      // Patch iScroll's _end method to augment click events
      const originalEnd = iScrollInstance.current._end
      iScrollInstance.current._end = function (e:TouchEvent|PointerEvent|MouseEvent) {
      if (!deduplicateEvent(e)) {
            return
          }
        // Find the React event handler system's synthetic event dispatcher
        const target = e.target as HTMLElement
        // Manually trigger React's synthetic event system (if needed)
        // (This is a simplified approach; React's event system is complex.)
        if (target.dispatchEvent) {
          let customEvent: CustomEvent;
         if('touches' in e && e.changedTouches.length > 0){
             customEvent = new CustomEvent(scrollerClickEvent, {
              bubbles: true,
              detail: { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY, scrollId: scrollId }
            })
            target.dispatchEvent(customEvent)
          }
          else if('touches' in e && e.touches.length > 0){
            customEvent = new CustomEvent(scrollerClickEvent, {
             bubbles: true,
             detail: { x: e.touches[0].clientX, y: e.touches[0].clientY, scrollId: scrollId  }
           })
           target.dispatchEvent(customEvent)
         }
         else if('pointerId' in e){
          customEvent = new CustomEvent(scrollerClickEvent, {
           bubbles: true,
           detail: { x: (e as PointerEvent).clientX, y: (e as PointerEvent).clientY, scrollId: scrollId  }
         })
         target.dispatchEvent(customEvent)
       }
      //handle MouseEvent
       else if (e instanceof MouseEvent) {
        customEvent = new CustomEvent(scrollerClickEvent, {
          bubbles: true,
          detail: { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY, scrollId: scrollId  }
        })
        target.dispatchEvent(customEvent)
      }
        }
        // Call original handler
        originalEnd.call(this, e)
      }
      return () => {
        if (iScrollInstance.current) {
          iScrollInstance.current.off('scrollStart', notifyScrollStart)
          iScrollInstance.current.off('scroll', scheduleScroll)
          iScrollInstance.current.off('scrollStart', startPolling)
          iScrollInstance.current.off('scrollEnd', handleScrollEnd)
          iScrollInstance.current.destroy()
          iScrollInstance.current = null
        }
        if (rafId !== null) {
          cancelSchedule(rafId)
        }
        stopPolling()
      }
    }
  }, [isTouchDevice, options, scrollId])

  useEffect(() => {
    const containerRef = scrollContainerRef.current
    containerRef?.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)
    document.addEventListener('hidden.bs.modal', handleModalClose)
    containerRef?.addEventListener('mousedown', preventMousedown, {
      passive: false
    })
    return () => {
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('hidden.bs.modal', handleModalClose)
      containerRef?.removeEventListener('mousedown', preventMousedown)
      containerRef?.removeEventListener('scroll', handleScroll)
    }
  }, [handleModalClose, handleResize, handleScroll, preventMousedown])

  useEffect(() => {
    if (!autoRefreshOnChildrenChange) {
      return
    }
    if (iScrollInstance.current) {
      iScrollInstance.current.refresh()
    }
  }, [autoRefreshOnChildrenChange, children])

  const containerClasses = [
    'scroll-wrapper__container',
    horizontal ? 'scroll-wrapper__container--horizontal' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ')

  const contentClasses = [
    'scroll-wrapper__content',
    horizontal ? 'scroll-wrapper__content--horizontal' : '',
    contentClassName ?? ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      ref={scrollContainerRef}
      className={containerClasses}
      style={style}
    >
      <div
        ref={contentRef}
        className={contentClasses}
        style={contentStyle}
      >
        {children}
      </div>
    </div>
  )
})

export default memo(ScrollWrapper)
